// src/hooks/useModuleMode.js
//
// Resolves which web modules (Voice, Browser, Chats, etc.) to show in the
// nav for the current user. Reads from `userProfile.moduleMode` (DB-backed
// durable preference, set by backend ensureUserRecord on first signup or
// by the Settings module-mode picker). Falls back to `'all'` for users
// without the field (existing users — no regression).
//
// A localStorage override (`bm_module_mode_override`) lets power users flip
// instantly between modes without round-tripping the backend.

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const VALID_MODES = ['voice', 'browser', 'chats', 'all'];
const OVERRIDE_KEY = 'bm_module_mode_override';

/**
 * Returns the effective module mode for the current user.
 *   - 'all'     → show every nav item (existing behavior)
 *   - 'voice'   → Voice-focused nav (Voices, Notes, Ask, Settings, Help)
 *   - 'browser' → Browser-focused nav (Sessions, AI Chats, Bookmarks, Notes, Ask)
 *   - 'chats'   → Chats-focused (AI Chats, Notes, Ask)
 *
 * @returns {{ mode: string, setOverride: (m: string|null) => void }}
 */
export function useModuleMode() {
  const { userProfile } = useAuth();

  const mode = useMemo(() => {
    // Override wins (instant flip via Show-all toggle or test).
    try {
      const override = (typeof window !== 'undefined' && window.localStorage)
        ? window.localStorage.getItem(OVERRIDE_KEY)
        : null;
      if (override && VALID_MODES.includes(override)) return override;
    } catch { /* noop */ }

    // DB-backed preference next.
    if (userProfile && userProfile.moduleMode && VALID_MODES.includes(userProfile.moduleMode)) {
      return userProfile.moduleMode;
    }

    // Existing users (no moduleMode field) → 'all' — preserves current UI.
    return 'all';
  }, [userProfile]);

  const setOverride = (next) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      if (next === null || next === undefined) {
        window.localStorage.removeItem(OVERRIDE_KEY);
      } else if (VALID_MODES.includes(next)) {
        window.localStorage.setItem(OVERRIDE_KEY, next);
      }
      // Force a re-render — listeners on this hook need to pick up the change.
      window.dispatchEvent(new Event('bm-module-mode-changed'));
    } catch { /* noop */ }
  };

  return { mode, setOverride };
}

/** Nav-item visibility table per mode. Paths must match AppShell TOP_NAV/MORE_NAV. */
export const MODULE_NAV_VISIBILITY = {
  all: () => true,
  voice: (path) => ['/voice', '/voices', '/inbox', '/ask', '/settings', '/help'].includes(path),
  browser: (path) => ['/browser', '/sessions', '/chats', '/bookmarks', '/inbox', '/ask', '/settings', '/help'].includes(path),
  chats: (path) => ['/chats', '/inbox', '/ask', '/settings', '/help'].includes(path)
};

export const VALID_MODULE_MODES = VALID_MODES;
export const MODULE_MODE_OVERRIDE_KEY = OVERRIDE_KEY;
